/**
 * Epic #228 P5 (PD1): the ONE external-link rule. A leaf module: it imports
 * types only, so `slots.ts`, `public-dto.ts`, `resolve.ts`, `manifest.ts`,
 * `destinations.ts`, `element-capabilities.ts` and the ads validator can all
 * share it without a cycle.
 *
 * The rule is a pure predicate (`input → input | null`); nothing on a server
 * path rewrites a URL, so what is validated, stored and emitted is one
 * string. It is mirrored byte for byte by SQL
 * `website_internal.website_safe_external_url`; both run against
 * `__fixtures__/external-url-boundaries.json`.
 *
 * Grammar (ASCII only, `1 ≤ length ≤ 256`):
 * - literal lowercase `https://`;
 * - host ∈ {instagram.com, www.instagram.com, facebook.com,
 *   www.facebook.com, m.facebook.com}, then the end, `/`, `?` or `#` (so no
 *   port, userinfo, trailing dot, other subdomain or IDN);
 * - path, query and fragment characters ∈ `[A-Za-z0-9._~!$&'()*+,;=:@%/?#-]`;
 * - every `%` is followed by two hex digits;
 * - no Meta first-party redirector path (security WARNING 1, devils-advocate
 *   major 2): the first path segment is not `l.php`, `login.php`, `login`,
 *   `flx`, `linkshim`, `dialog` or `plugins` (any letter case, ended by `/`,
 *   `?`, `#`, `;` or the end), it holds no `%` escape (so no encoded
 *   `l%2Ephp`) and is not empty (`//l.php`), and no path segment is `.` or
 *   `..` (`/x/../l.php`), and (fix round 2) the path holds no `%2e`, `%2f`,
 *   `%5c` or `%25` escape in any case: browsers resolve `/x/%2E%2E/l.php` to
 *   `/l.php`, servers may decode `%2f`/`%5c` as separators, and `%25` only
 *   serves double encoding. Allowed characters stay exactly as above, so the
 *   rule remains a predicate; the denied names are matched with explicit
 *   case classes, never by lowercasing the input.
 */
import type { WebsiteDestinationKind, WebsiteSlotSchema } from '../../types/website-templates';

/** The public DTO parser's href cap; a longer value would fail its parse. */
export const WEBSITE_EXTERNAL_URL_MAX_LENGTH = 256;

export const WEBSITE_EXTERNAL_URL_HOSTS = [
  'instagram.com',
  'www.instagram.com',
  'facebook.com',
  'www.facebook.com',
  'm.facebook.com',
] as const;

/** Why a URL is refused. Client copy only; SQL answers allowed / not allowed. */
export type ExternalUrlRejection = 'https' | 'host' | 'length' | 'syntax';

const SCHEME = 'https://';
const HOST_RE =
  /^https:\/\/(?:instagram\.com|www\.instagram\.com|facebook\.com|www\.facebook\.com|m\.facebook\.com)(?:$|[/?#])/;
const SAFE_URL_RE =
  /^https:\/\/(?:instagram\.com|www\.instagram\.com|facebook\.com|www\.facebook\.com|m\.facebook\.com)(?:[/?#][A-Za-z0-9._~!$&'()*+,;=:@%/?#-]*)?$/;
const BAD_ESCAPE_RE = /%(?![0-9A-Fa-f]{2})/;
// `deniedPath` of external-url-boundaries.json (a JS literal escapes `/`) and
// the third regex of SQL `website_safe_external_url`.
const DENIED_PATH_RE =
  /^https:\/\/[^/?#]+(?:\/(?:(?:[Ll]\.[Pp][Hh][Pp]|[Ll][Oo][Gg][Ii][Nn]\.[Pp][Hh][Pp]|[Ll][Oo][Gg][Ii][Nn]|[Ff][Ll][Xx]|[Ll][Ii][Nn][Kk][Ss][Hh][Ii][Mm]|[Dd][Ii][Aa][Ll][Oo][Gg]|[Pp][Ll][Uu][Gg][Ii][Nn][Ss])(?:[/?#;]|$)|[^/?#]*%|\/)|(?:\/[^/?#]*)*\/\.\.?(?:[/?#]|$)|[^?#]*%(?:2[EeFf]|5[Cc]|25))/;

/**
 * Why `url` is refused, in a fixed order: length, then scheme, then host,
 * then characters and escapes, then a redirector path (reported as `host`:
 * the link would leave Instagram/Facebook). `ok` is exactly
 * `isAllowedSocialUrl(url)`.
 */
export function explainSocialUrl(
  url: string,
): { readonly ok: true } | { readonly ok: false; readonly reason: ExternalUrlRejection } {
  if (url.length === 0 || url.length > WEBSITE_EXTERNAL_URL_MAX_LENGTH) {
    return { ok: false, reason: 'length' };
  }
  if (!url.startsWith(SCHEME)) return { ok: false, reason: 'https' };
  if (!HOST_RE.test(url)) return { ok: false, reason: 'host' };
  if (!SAFE_URL_RE.test(url) || BAD_ESCAPE_RE.test(url)) return { ok: false, reason: 'syntax' };
  if (DENIED_PATH_RE.test(url)) return { ok: false, reason: 'host' };
  return { ok: true };
}

/** The external-link predicate (TS twin of SQL `website_safe_external_url`). */
export function isAllowedSocialUrl(url: unknown): url is string {
  return typeof url === 'string' && explainSocialUrl(url).ok;
}

const AUTHORITY_RE = /^([A-Za-z][A-Za-z0-9+.-]*:\/\/)([^/?#]*)/;

/**
 * Editor-only input clean-up, run BEFORE validation: trims the ends and
 * lowercases the scheme and the authority. The path, query and fragment are
 * never touched (Instagram handles are case-insensitive, post ids are not).
 */
export function normalizeSocialUrlInput(raw: string): string {
  const trimmed = raw.trim();
  return trimmed.replace(
    AUTHORITY_RE,
    (_match, scheme: string, authority: string) =>
      `${scheme.toLowerCase()}${authority.toLowerCase()}`,
  );
}

/**
 * The destination kinds one link slot admits: the manifest's `linkTargets`
 * in their order, then `external` unless the manifest opts out with
 * `externalLinks: false` (PD4). The one TS implementation of the platform
 * rule; SQL applies the identical expansion in `website_validate_slot_value`.
 */
export function websiteAllowedLinkKinds(
  schema: Pick<WebsiteSlotSchema, 'linkTargets' | 'externalLinks'>,
): readonly WebsiteDestinationKind[] {
  return [
    ...(schema.linkTargets ?? []),
    ...(schema.externalLinks === false ? [] : ['external' as const]),
  ];
}
